import classNames from "classnames";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import {
    INVOICE_SCHEDULE_BLOCK_MESSAGES,
    InvoiceScheduleBlockReason,
} from "@common/helpers/domains/accounting/invoiceScheduleEligibility";
import { pluralizeWithCount } from "@common/helpers/string";
import { MatchedScheduleCell } from "@components/accounting/schedules/MatchedScheduleCell";
import { ScheduleCard } from "@components/accounting/schedules/ScheduleCard";
import {
    InteractiveScheduleCellState,
    ScheduleCellState,
    ScheduleUnavailableReason,
} from "./scheduleCellState";
import styles from "./ScheduleCellContent.module.scss";

const UNAVAILABLE_TOOLTIPS: Record<
    ScheduleUnavailableReason | InvoiceScheduleBlockReason,
    string
> = {
    ...INVOICE_SCHEDULE_BLOCK_MESSAGES,
    [ScheduleUnavailableReason.derivedLine]:
        "Tax and discount lines follow the line they came from",
    [ScheduleUnavailableReason.zeroAmount]: "There is no amount to schedule",
};

interface Props {
    state: Exclude<ScheduleCellState, InteractiveScheduleCellState>;
}

/** The read-only Schedule cell states; SchedulePicker owns "+ Add" and the own-schedule card. */
export const ScheduleCellContent = ({ state }: Props) => {
    if (state.kind === "matched") {
        return (
            <MatchedScheduleCell
                schedules={state.schedules}
                counterpart="transaction"
            />
        );
    }

    if (state.kind === "wholeInvoice") {
        return (
            <ScheduleCard
                schedule={state.schedule}
                tooltipContent="The whole invoice is scheduled. Switch to By invoice to change it."
            />
        );
    }

    if (state.kind === "partial") {
        return (
            <Tooltip content="Some lines are scheduled. Switch to By line item to see or change them.">
                <span className={classNames(styles.muted, styles.partial)}>
                    {`Partially scheduled (${pluralizeWithCount(
                        "line",
                        state.scheduledLineCount,
                    )})`}
                </span>
            </Tooltip>
        );
    }

    return (
        <Tooltip content={UNAVAILABLE_TOOLTIPS[state.reason]}>
            <span className={styles.muted}>-</span>
        </Tooltip>
    );
};
